import React from 'react';

import { Box, COLORS, Text } from '@clutter/clean';

import { useMovingCheckoutContext } from '@root/components/checkout/context';
import {
  MovingCheckoutStepProps,
  MovingStepDescriptor,
} from '@root/components/checkout/types';
import { ClientAddress } from '@root/resources/types/address';

import { FreeStorageBanner } from '../../subcomponents/free_storage_banner';
import {
  AddressField,
  MovingAddressFields,
  sanitizeAddressInput,
} from '../../subcomponents/moving_address_fields';

export const addressCompleted: MovingStepDescriptor['resolveCompleted'] = ({
  values,
  validMoveRoute,
}) =>
  !!(
    sanitizeAddressInput(values.startAddress) &&
    sanitizeAddressInput(values.endAddress) &&
    !!validMoveRoute
  );

export const Address: React.FC<MovingCheckoutStepProps> = ({
  onChange,
  values: { startAddress, endAddress, startAddressDetails, rooms },
  scrollToStep,
}) => {
  const { createQuote } = useMovingCheckoutContext();

  const didCompleteRoute = (field: AddressField, value: ClientAddress) =>
    (field === 'endAddress' &&
      value.state &&
      value.street &&
      startAddress?.state) ||
    (field === 'startAddress' &&
      value.state &&
      value.street &&
      endAddress?.state);

  const handleAddressChange = async (
    field: AddressField,
    value: ClientAddress,
  ) => {
    onChange(field, value);
    if (field === 'startAddress' && value.zip) {
      onChange('zip', value.zip);
    }
    if (didCompleteRoute(field, value)) {
      onChange('adjustedMoverCount', undefined, () => createQuote());
      scrollToStep();
    }
    onChange('unpackingScheduled', undefined);

    onChange('moverCountSelected', undefined);
    onChange('movingBundle', undefined);
  };

  return (
    <Box margin={['48px 0 0', null, '72px 0 0']}>
      <Text.Title color={COLORS.tealDark} size="small">
        Where are you moving?
      </Text.Title>
      <Box margin="4px 0 24px">
        <Text.Body color={COLORS.storm}>
          We’ll pick up your items, help you pack them if you need to and move
          them securely to your new location.
        </Text.Body>
      </Box>
      <Box.Flex flexDirection="column" gap="20px">
        <MovingAddressFields
          startAddress={startAddress}
          endAddress={endAddress}
          startAddressDetails={startAddressDetails}
          rooms={rooms}
          flowOnChange={onChange}
          onChange={handleAddressChange}
        />
      </Box.Flex>
      <Box margin="36px 0 0">
        <FreeStorageBanner />
      </Box>
    </Box>
  );
};
